import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForgotPassword } from '@field-tickets/shared';
import { Spinner } from '@/components/Spinner';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const mutation = useForgotPassword();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mutation.isPending) return; // double-submit guard

    // Self-aware redirect so a reset triggered from a LAN/Tailscale dev URL
    // returns to the same origin instead of falling back to production.
    const redirectTo =
      typeof window !== 'undefined'
        ? `${window.location.origin}/reset-password`
        : undefined;

    // Use mutate() (fire-and-forget) so React Query owns the rejection — the
    // previous implementation awaited mutateAsync without try/catch which
    // surfaced as an "uncaught (in promise)" warning on every backend error.
    // The `mutation.isError` UI already covers the visible failure path.
    mutation.mutate({ email, redirectTo }, {
      onSuccess: () => setSent(true),
    });
  };

  return (
    <div className="login-page">
      <svg className="login-logo" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>

      <h1 className="login-title">Reset password</h1>

      {sent ? (
        <div role="status" aria-live="polite">
          <p className="login-subtitle" style={{ maxWidth: 320 }}>
            If an account exists with that email, a password reset link has been sent. Check your inbox.
          </p>
          <Link to="/login" className="login-btn" style={{ textDecoration: 'none', display: 'inline-block', maxWidth: 320, width: '100%', textAlign: 'center', marginTop: 'var(--space-4)' }}>
            Back to Sign In
          </Link>
        </div>
      ) : (
        <>
          <p className="login-subtitle">
            Enter your email and we'll send you a reset link.
          </p>

          {mutation.isError && (
            <div role="alert" style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
              Something went wrong. Please try again.
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 320 }}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={mutation.isPending}
              style={{ marginBottom: 'var(--space-3)' }}
            >
              {mutation.isPending ? <Spinner size="sm" /> : 'Send Reset Link'}
            </button>
          </form>

          <Link
            to="/login"
            style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}
          >
            Back to Sign In
          </Link>
        </>
      )}
    </div>
  );
}
